import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, tap } from 'rxjs';
import { BrowserService } from '../services/browser.service';
import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Normalize API error body text for matching (backend: "Token expired, please login again", etc.) */
function authFailureMessage(err: HttpErrorResponse): string {
  const body = err.error;
  if (typeof body === 'string') return body.toLowerCase();
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const m = o['message'] ?? o['error'];
    return String(m ?? '').toLowerCase();
  }
  return '';
}

/** JWT is past exp or cryptographically invalid — client activity timers cannot fix this; must re-login */
function isStaleOrInvalidJwt(err: HttpErrorResponse): boolean {
  const msg = authFailureMessage(err);
  if (!msg) return false;
  return (
    msg.includes('token expired') ||
    msg.includes('invalid token') ||
    (msg.includes('expired') && msg.includes('login')) ||
    (msg.includes('invalid') && msg.includes('login'))
  );
}

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const browserService = inject(BrowserService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  return next(req).pipe(
    // Update activity time on successful requests (user is active)
    tap(() => {
      if (isBrowser) {
        // Check if user is logged in before updating activity
        const user = browserService.getLocalStorage('loggedInUser');
        if (user) {
          // Throttle updates to once per 30 seconds
          const lastUpdate = sessionStorage.getItem('lastActivityUpdate');
          const now = Date.now();
          if (!lastUpdate || (now - parseInt(lastUpdate)) > 30000) {
            sessionStorage.setItem('lastActivityTime', now.toString());
            sessionStorage.setItem('lastActivityUpdate', now.toString());
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Only handle 401 errors
      if (error.status === 401 && isBrowser) {
        const url = req.url.toLowerCase();
        const currentUrl = router.url.toLowerCase();
        const now = Date.now(); // Declare once at the start of the block
        
        // Clear stale delivery agent session on API auth failure
        if (url.includes('/delivery-profile/') && !url.includes('/delivery-profile/auth/')) {
          browserService.removeLocalStorage('delivery_token');
          browserService.removeLocalStorage('delivery_agent');
        }

        // Skip handling for login/auth endpoints to avoid redirect loops
        if (
          url.includes('/login') ||
          url.includes('/signup') ||
          url.includes('/auth/') ||
          url.includes('/delivery-profile/auth/')
        ) {
          return throwError(() => error);
        }

        // Skip if already on a login page to prevent redirect loops
        if (currentUrl.includes('/login') || currentUrl.includes('/seller-login') || 
            currentUrl.includes('/sellar-login') || currentUrl.includes('/customer/login') ||
            currentUrl.includes('/courier/login') || currentUrl.includes('/admin/login') ||
            currentUrl.includes('/delivery/login') || currentUrl.includes('/delivery/signup') ||
            currentUrl.startsWith('/delivery')) {
          return throwError(() => error);
        }

        // Grace period after login - ignore 401s for 10 seconds after login
        const lastLoginTime =
          sessionStorage.getItem('deliveryLastLoginTime') || sessionStorage.getItem('lastLoginTime');
        if (lastLoginTime) {
          const timeSinceLogin = now - parseInt(lastLoginTime);
          const GRACE_PERIOD_MS = 10000; // 10 seconds grace period after login
          if (timeSinceLogin < GRACE_PERIOD_MS) {
            return throwError(() => error);
          }
        }

        // JWT expired/invalid: never pretend the session is OK just because the user clicked recently
        const jwtMustReauth = isStaleOrInvalidJwt(error);

        // Check session inactivity - only expire after 30 minutes of inactivity (does not apply to dead JWTs)
        const lastActivityTime = sessionStorage.getItem('lastActivityTime');
        const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds

        if (!jwtMustReauth) {
          if (lastActivityTime) {
            const timeSinceActivity = now - parseInt(lastActivityTime);
            if (timeSinceActivity < SESSION_TIMEOUT_MS) {
              // Session is still valid (within 30 minutes of activity)
              // User is actively using the site - don't expire session
              // This might be a temporary network issue, permission error, or backend issue
              return throwError(() => error);
            } else {
              // More than 30 minutes of inactivity - session should expire
              }
          } else {
            // No activity tracking - might be first request or activity tracking not initialized
            // Only expire if it's clearly a session expiry error
            const errorMessage = (error.error?.message || error.error?.error || '').toLowerCase();
            const isSessionExpired =
              errorMessage.includes('token') &&
              (errorMessage.includes('expired') || errorMessage.includes('invalid'));

            if (!isSessionExpired) {
              return throwError(() => error);
            }
          }
        } else {
          console.warn('401: JWT expired or invalid — clearing session and redirecting to login');
        }

        // Skip for polling/background requests that might fail for other reasons
        // These are typically analytics, stats, or real-time update endpoints
        const isPollingRequest = url.includes('/analytics') || 
                                 url.includes('/stats') || 
                                 url.includes('/realtime') ||
                                 url.includes('/notifications') ||
                                 url.includes('/updates') ||
                                 url.includes('/finance/details') ||
                                 url.includes('/seller/stats');

        // Check if this is a token validation error (not just any 401)
        // Only logout if the error message indicates authentication failure
        const errorMessage = authFailureMessage(error);
        const isAuthError = errorMessage.includes('token') || 
                           errorMessage.includes('unauthorized') ||
                           errorMessage.includes('authentication') ||
                           errorMessage.includes('login') ||
                           errorMessage.includes('session') ||
                           errorMessage.includes('expired');

        // For polling requests, only redirect if it's clearly an auth error
        // For other requests, be more lenient but still check
        if (!isAuthError) {
          // If it's a polling request and not clearly an auth error, skip redirect
          if (isPollingRequest) {
            console.warn('401 error on polling request but not an auth error, skipping redirect:', errorMessage || 'No error message');
            return throwError(() => error);
          }
          
          // For non-polling requests, if there's an error message but it's not auth-related, skip
          if (errorMessage) {
            console.warn('401 error but not an auth error, skipping logout:', errorMessage);
            return throwError(() => error);
          }
          
          // If no error message at all, it might be a network issue or permission problem
          // Only redirect if we're on a protected route (seller dashboard, etc.)
          const isProtectedRoute = currentUrl.includes('/seller-dashboard') || 
                                   currentUrl.includes('/customer/') ||
                                   currentUrl.includes('/courier/') ||
                                   currentUrl.includes('/admin/') ||
                                   currentUrl.startsWith('/delivery');
          
          if (!isProtectedRoute) {
            console.warn('401 error on non-protected route with no error message, skipping redirect');
            return throwError(() => error);
          }
        }

        // Prevent multiple redirects - check if we've already redirected recently
        const lastRedirectTime = sessionStorage.getItem('lastAuthRedirect');
        if (lastRedirectTime && (now - parseInt(lastRedirectTime)) < 2000) {
          // Redirected within last 2 seconds, skip to prevent loops
          console.warn('Recent redirect detected, skipping to prevent loop');
          return throwError(() => error);
        }
        sessionStorage.setItem('lastAuthRedirect', now.toString());

        // Clear tokens and user data
        browserService.removeLocalStorage('token');
        browserService.removeLocalStorage('loggedInUser');
        browserService.removeLocalStorage('rememberedUser');
        browserService.removeLocalStorage('courier_token');
        browserService.removeLocalStorage('customer_token');

        // Determine redirect path based on current route and user type
        // Check if user is a seller by looking at stored user data
        let redirectPath = '/login'; // Default to regular user login
        
        // Check if user is a seller (has seller token or is on seller routes)
        const storedUser = browserService.getLocalStorage('loggedInUser');
        let isSeller = false;
        
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            isSeller = user?.isSeller === true || user?.role === 'seller';
          } catch (e) {
            // Ignore parsing errors
          }
        }
        
        // Check route-based indicators
        if (currentUrl.includes('/seller-dashboard') || currentUrl.includes('/seller/') || isSeller) {
          redirectPath = '/sellar-login';
        } else if (currentUrl.includes('/customer/')) {
          redirectPath = '/customer/login';
        } else if (currentUrl.includes('/courier/')) {
          redirectPath = '/courier/login';
        } else if (currentUrl.includes('/delivery/')) {
          redirectPath = '/delivery/login';
        } else if (currentUrl.includes('/admin/')) {
          redirectPath = '/admin/login';
        }

        // Only redirect if not already on the target login page
        if (!currentUrl.includes(redirectPath.replace('/', ''))) {
          // Redirect to login with return URL
          router.navigate([redirectPath], {
            queryParams: {
              returnUrl: router.url,
              reason: 'session_expired'
            }
          }).catch(err => {
            console.error('Navigation error:', err);
          });
        }
      }

      return throwError(() => error);
    })
  );
};


